import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  Container, 
  FormControl, 
  InputLabel, 
  MenuItem, 
  Select,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Chip,
  Modal
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import Autocomplete from '@mui/material/Autocomplete';

import axios from 'axios';
import baseURL from '../../baseURL';
import GirviPageShell from '../common/GirviPageShell';
import { PledgePhotosRow } from '../common/PhotoThumb';
import { fingerprintService } from '../../services/fingerprintService';
import { printDocument } from '../../utils/printUtility';
import ReleasePrint from '../print/templates/ReleasePrint';
import { compressImageFileUnder10Kb } from '../../utils/compressImageUnder10Kb';
import {
  computeInterest,
  calendarDaysBetween,
  resolvePledgeDhara
} from '../../utils/dharaInterest';
import { interestForSession, isBlackLogin, rateLabel, secondaryReleasePosting } from '../../utils/blackInterest';
import { DEFAULT_DHARA } from '../../constants/dhara';

/** Interest-style day count: 12×30-day months per year (e.g. same calendar date +4y => 1440, not 1461). */
function releaseDisplayDays30By360(pledgeDate, releaseDate) {
  const s = pledgeDate instanceof Date ? pledgeDate : new Date(pledgeDate);
  const e = releaseDate instanceof Date ? releaseDate : new Date(releaseDate);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
  const y1 = s.getFullYear();
  const m1 = s.getMonth() + 1;
  const d1 = s.getDate();
  const y2 = e.getFullYear();
  const m2 = e.getMonth() + 1;
  const d2 = e.getDate();
  return Math.max(0, 360 * (y2 - y1) + 30 * (m2 - m1) + (d2 - d1));
}

const ReleasePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editReleaseId = searchParams.get('edit');
  const [isEditMode, setIsEditMode] = useState(false);
  const [ticketNumber, setTicketNumber] = useState('');
  const [pledgeData, setPledgeData] = useState(null);
  const [releaseDate, setReleaseDate] = useState(new Date());
  const [days, setDays] = useState(0);
  const [error, setError] = useState('');
  const [adjustedPrincipal, setAdjustedPrincipal] = useState(0);
  const [receiptAmount, setReceiptAmount] = useState(0);
  const [showConfirmBox, setShowConfirmBox] = useState(false);
  const [continueInput, setContinueInput] = useState('');
  const [searchType, setSearchType] = useState('firstName');
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showReleasePhotoDialog, setShowReleasePhotoDialog] = useState(false);
  const [pendingPaymentForRelease, setPendingPaymentForRelease] = useState(null);
  const [releaseCustomerPhotoDataUrl, setReleaseCustomerPhotoDataUrl] = useState(null);
  const [releaseArticlePhotoDataUrl, setReleaseArticlePhotoDataUrl] = useState(null);
  const [compressingReleasePhotos, setCompressingReleasePhotos] = useState(false);
  const [lastReleasePrintPayload, setLastReleasePrintPayload] = useState(null);
  const [releasePhotoCustFile, setReleasePhotoCustFile] = useState(null);
  const [releasePhotoArtFile, setReleasePhotoArtFile] = useState(null);
  const [releaseData, setReleaseData] = useState(null);
  const [isVerifyingFingerprint, setIsVerifyingFingerprint] = useState(false);
  const [fingerprintVerified, setFingerprintVerified] = useState(false);
  const [fingerprintScore, setFingerprintScore] = useState(null);
  const [verifiedFingerprintTemplate, setVerifiedFingerprintTemplate] = useState(null);

  // Payment mode dialog states
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentRows, setPaymentRows] = useState([{ type: 'cash', bankLedgerCode: '', bankLedgerName: '', amount: '' }]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);

  const fetchBankAccounts = async () => {
    try {
      setLoadingBanks(true);
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${baseURL}/ledgers/bank-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBankAccounts(resp?.data?.data || []);
    } catch (err) {
    } finally {
      setLoadingBanks(false);
    }
  };

  // Helper function to format date as dd/mm/yyyy
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Calculate total received interest
  const totalReceivedInterest = () => {
    if (!pledgeData?.receipts) return 0;
    return pledgeData.receipts.reduce((total, receipt) => {
      const interestDetails = receipt?.interestDetails || {};
      return total + (interestDetails.calculatedInterest || 0);
    }, 0);
  };

  // Ensure body scroll is restored when entering Release (avoids stuck state from other screens)
  useEffect(() => {
    document.body.style.overflow = 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  // Load release data for edit mode
  useEffect(() => {
    const fetchReleaseForEdit = async () => {
      if (editReleaseId) {
        try {
          setIsEditMode(true);
          const token = localStorage.getItem('token');
          const response = await fetch(`${baseURL}/releases/${editReleaseId}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            const release = data.release || data.data || data;
            
            if (release) {
              // Set release date
              if (release.releaseDate) {
                setReleaseDate(new Date(release.releaseDate));
              }
              
              // Set ticket number and fetch pledge
              if (release.ticketNo) {
                setTicketNumber(release.ticketNo);
                await fetchPledgeData(release.ticketNo);
              } else if (release.pledgeId?.loanDetails?.ticketNo) {
                setTicketNumber(release.pledgeId.loanDetails.ticketNo);
                await fetchPledgeData(release.pledgeId.loanDetails.ticketNo);
              }
            }
          }
        } catch (error) {
        }
      }
    };

    fetchReleaseForEdit();
  }, [editReleaseId]);

  const fetchPledgeData = async (ticketNo) => {
    try {
      const token = localStorage.getItem('token');
      // Use search API with ticket number instead of direct pledge fetch
      const response = await axios.get(`${baseURL}/pledges/search`, {
        params: {
          searchType: 'ticketNo',
          searchValue: ticketNo
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
  
      // Get the first result (should be only one match)
      const pledge = response.data.data.pledges[0];
      if (pledge) {
        setPledgeData(pledge);
        setFingerprintVerified(false);
        setFingerprintScore(null);
        setVerifiedFingerprintTemplate(null);
        setError('');
      } else {
        setError('Pledge not found');
        setPledgeData(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching pledge data');
      setPledgeData(null);
    }
  };

  const calculateInterest = () => {
    if (!pledgeData?.loanDetails?.principalAmount || !releaseDate) return 0;

    const pledgeDate = new Date(pledgeData.date);
    const releaseDateObj = new Date(releaseDate);
    const actualDays = calendarDaysBetween(pledgeDate, releaseDateObj);
    if (actualDays <= 0) return 0;

    const dhara = resolvePledgeDhara(pledgeData, DEFAULT_DHARA);
    if (!pledgeData?.loanDetails?.rateOfInterest) return 0;

    return interestForSession(pledgeData, {
      actualDays,
      dhara,
      black: false
    });
  };

  // Secondary book only. Shown on a black-password login. Not added to the legal total.
  const calculateSecondaryPosting = () => {
    if (!isBlackLogin() || !pledgeData?.date || !releaseDate) return 0;
    const pledgeDate = new Date(pledgeData.date);
    const releaseDateObj = new Date(releaseDate);
    return secondaryReleasePosting(pledgeData, {
      startDate: pledgeDate,
      endDate: releaseDateObj,
      legalInterest: calculateInterest(),
      receipts: pledgeData.receipts || []
    });
  };

  const calculateTotalAmount = () => {
    if (!pledgeData || !days) return '0.00';
    const interest = calculateInterest();
    const totalAmount =
      (pledgeData?.loanDetails?.principalAmount || 0) + parseFloat(interest);

    return totalAmount.toFixed(2);
  };
  const searchTimeout = useRef(null);
  const handleSearch = async (value, type = null) => {
    try {
      setError('');
      setSearchLoading(true);
      
      // If type is provided, update searchType and searchValue
      if (type) {
        setSearchType(type);
        setSearchValue(value);
      }
  
      if (!value.trim()) {
        setError('Please enter a search value');
        setSearchLoading(false);
        return;
      }


  
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseURL}/pledges/search`, {
        params: {
          searchType: type || searchType,
          searchValue: value.trim()
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
  
      setSearchResults(response.data.data.pledges || []);
      setSearchLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error searching pledges');
      setSearchLoading(false);
    }
  };

  const handleFingerprintSearch = async () => {
    try {
      setError('');
      setSearchLoading(true);
      const result = await fingerprintService.searchPledgesByScan(60);
      const matches = result?.matches || [];

      if (matches.length > 0) {
        const customerCode = matches[0]?.customer?.code;
        if (customerCode) {
          const token = localStorage.getItem('token');
          const resp = await axios.get(`${baseURL}/pledges/client/${customerCode}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const allPledges = resp?.data?.pledges || [];
          const activePledges = allPledges.filter(
            p => p.status !== 'RELEASED' && !p.loanDetails?.released
          );
          setSearchResults(activePledges);
          if (activePledges.length === 0) {
            setError('Fingerprint matched but no active pledges found for this customer.');
          }
        } else {
          setSearchResults(matches);
        }
      } else {
        setSearchResults([]);
        setError('No pledges matched this fingerprint.');
      }
    } catch (err) {
      setError(err.message || 'Error searching pledges by fingerprint');
    } finally {
      setSearchLoading(false);
    }
  };
  const handleTicketNumberChange = (e) => {
    setTicketNumber(e.target.value);
  };

  const handleCompleteTransaction = async (paymentModes = [], custPhotoUrl, artPhotoUrl) => {
    try {
      setLoading(true);
      setError('');
      
      if (!pledgeData || !pledgeData?.loanDetails || !pledgeData?.loanDetails?.ticketNo) {
        setError('Invalid pledge data. Please search for the pledge again.');
        return;
      }
      if (!pledgeData?._id) {
        setError('Pledge ID missing. Please search the pledge again.');
        return;
      }

      const customerPhotoData = custPhotoUrl === undefined ? releaseCustomerPhotoDataUrl : custPhotoUrl;
      const articlePhotoData = artPhotoUrl === undefined ? releaseArticlePhotoDataUrl : artPhotoUrl;

      const principal = Number(pledgeData?.loanDetails?.principalAmount) || 0;
      const interest = calculateInterest() || 0;
      const totalAmount = principal + interest;
      
      const validPayments = paymentModes.filter(p => p.amount && Number(p.amount) > 0).map(p => ({
        type: p.type,
        bankLedgerCode: p.bankLedgerCode || null,
        bankLedgerName: p.bankLedgerName || null,
        amount: Number(p.amount)
      }));

      const releasePayload = {
        ticketNo: pledgeData?.loanDetails?.ticketNo,
        releaseDate: releaseDate ? releaseDate.toISOString() : new Date().toISOString(),
        principal: principal,
        interest: interest,
        total: totalAmount,
        totalPaid: 0,
        totalInterestPaid: 0,
        totalPrincipalPaid: 0,
        paymentModes: validPayments,
        ...(customerPhotoData ? { releaseCustomerPhotoData: customerPhotoData } : {}),
        ...(articlePhotoData ? { releaseArticlePhotoData: articlePhotoData } : {}),
        ...(verifiedFingerprintTemplate
          ? { fingerprintTemplate: verifiedFingerprintTemplate, fingerprintThreshold: 60 }
          : {})
      };


      // Save release to backend using fetch instead of axios
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/releases`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(releasePayload)
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || responseData.details || `HTTP error! status: ${response.status}`);
      }

      if (responseData.success) {
        setReleaseData(responseData.data);
        setLastReleasePrintPayload({
          pledgeData,
          releaseData: {
            ...(responseData.data?.release || responseData.data),
            paymentModes: validPayments
          }
        });
        setReleaseCustomerPhotoDataUrl(null);
        setReleaseArticlePhotoDataUrl(null);
        setPendingPaymentForRelease(null);
        setShowPrintModal(true);
      } else {
        throw new Error(responseData.message || 'Failed to save release');
      }
      
    } catch (error) {
      setError(error.message || 'Failed to complete transaction. Please try again.');
    } finally {
      setIsVerifyingFingerprint(false);
      setLoading(false);
    }
  };

  const handleScanAndMatchFingerprint = async () => {
    try {
      setError('');
      if (!pledgeData?._id) {
        setError('Please load a pledge first.');
        return;
      }
      if (!pledgeData?.fingerprint?.template) {
        setError('No enrolled fingerprint found for this pledge.');
        return;
      }

      setIsVerifyingFingerprint(true);
      const captured = await fingerprintService.capture();
      const verification = await fingerprintService.verifyWithPledge(pledgeData._id, captured.template);
      if (!verification?.verified) {
        setFingerprintVerified(false);
        setFingerprintScore(Number(verification?.score ?? 0));
        setVerifiedFingerprintTemplate(null);
        throw new Error(`Fingerprint not matched${verification?.score ? ` (score: ${verification.score})` : ''}`);
      }

      setFingerprintVerified(true);
      setFingerprintScore(Number(verification.score ?? 0));
      setVerifiedFingerprintTemplate(captured.template);
    } catch (error) {
      setError(error.message || 'Fingerprint scan/verification failed');
    } finally {
      setIsVerifyingFingerprint(false);
    }
  };

  const handlePrint = async () => {
    try {
      if (lastReleasePrintPayload?.pledgeData && lastReleasePrintPayload?.releaseData) {
        await printDocument('release', lastReleasePrintPayload, ReleasePrint);
      }
    } catch (err) {
      setError(err.message || 'Print failed');
    }
    window.location.reload();
  };

  const handleCloseModal = () => {
    setShowPrintModal(false);
    window.location.reload(); // Reload the page when closing the modal
  };

  const handleReleaseDateChange = (date) => {
    if (!date) {
      setReleaseDate(null);
      setDays(0);
      return;
    }

    const releaseDate = new Date(date);
    if (isNaN(releaseDate.getTime())) {
      setError('Invalid date selected');
      return;
    }

    setReleaseDate(releaseDate);
    if (pledgeData) {
      const pledgeDate = new Date(pledgeData.date);
      if (isNaN(pledgeDate.getTime())) {
        setError('Invalid pledge date');
        return;
      }

      const actualDays = calendarDaysBetween(pledgeDate, releaseDate);
      const dhara = resolvePledgeDhara(pledgeData, DEFAULT_DHARA);
      const { chargedDays } = computeInterest(
        Number(pledgeData?.loanDetails?.principalAmount) || 0,
        Number(pledgeData?.loanDetails?.rateOfInterest) || 0,
        actualDays,
        dhara
      );
      setDays(chargedDays);
    }
  };

  const openPaymentDialog = () => {
    setPaymentRows([{ type: 'cash', bankLedgerCode: '', bankLedgerName: '', amount: '' }]);
    setShowPaymentDialog(true);
    fetchBankAccounts();
  };

  const handleInterestTypeChange = (e) => {
    if (e.target.value.toUpperCase() === 'Y') {
      openPaymentDialog();
    } else if (e.target.value.toUpperCase() === 'N') {
      setContinueInput('');
    }
  };

  const handleKeyPress = (e) => {
    if ((e.key || '').toLowerCase() === 'y') {
      setShowPrintModal(true);
    } else if ((e.key || '').toLowerCase() === 'n') {
      // Handle cancel action
      navigate('/release');
    }
  };

  const PrintModal = () => (
    <Modal
      open={showPrintModal}
      onClose={handleCloseModal}
      aria-labelledby="print-modal-title"
      aria-describedby="print-modal-description"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 1,
        textAlign: 'center'
      }}>
        <Typography id="print-modal-title" variant="h6" component="h2" gutterBottom>
          Release Successful
        </Typography>
        <Typography id="print-modal-description" sx={{ mt: 2, mb: 4 }}>
          The release has been processed successfully. For A5 money-lender layout, set Master → Printer Settings → Release receipt to A5 and the desired template, then print.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handlePrint}
            startIcon={<PrintIcon />}
          >
            Print Receipt
          </Button>
          <Button 
            variant="outlined" 
            onClick={handleCloseModal}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );



  return (
    <>
      <GirviPageShell
        title="Create Release"
        subtitle="Pledge · Release"
        chipLabel="Pledge"
        maxWidth="xl"
        compact
      >
      <Box sx={{ mb: 1.5 }}>
  <Typography variant="h6" gutterBottom sx={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#1e3a5f', borderBottom: '1px solid rgba(201,162,77,0.35)', pb: 0.75, mb: 1.25 }}>
    Search Pledges
  </Typography>
  <Grid container spacing={1} alignItems="center">
    <Grid item xs={12} md={3}>
      <FormControl fullWidth size="small">
        <InputLabel>Search By</InputLabel>
        <Select
          value={searchType}
          onChange={(e) => {
            setSearchType(e.target.value);
            setSearchValue('');
            setSearchResults([]);
          }}
          label="Search By"
        >
          <MenuItem value="firstName">Customer Name</MenuItem>
          <MenuItem value="lastName">Last Name</MenuItem>
          <MenuItem value="customerCode">Customer Code</MenuItem>
          <MenuItem value="village">Village</MenuItem>
          <MenuItem value="date">Date (DD-MM-YYYY)</MenuItem>
          <MenuItem value="mobile">Mobile No</MenuItem>
          <MenuItem value="ticketNo">Ticket Number</MenuItem>
        </Select>
      </FormControl>
    </Grid>
    <Grid item xs={12} md={4}>
      <TextField
        fullWidth
        size="small"
        label="Search"
        value={searchValue}
        onChange={(e) => {
          const value = e.target.value;
          setSearchValue(value);
          handleSearch(value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && searchType === 'ticketNo' && searchValue) {
            setTicketNumber(searchValue);
            fetchPledgeData(searchValue);
          }
        }}
        placeholder={
          searchType === 'date'
            ? 'YYYY-MM-DD'
            : searchType === 'ticketNo'
            ? 'Ticket number'
            : 'Type to search'
        }
        InputProps={{
          endAdornment: searchLoading ? <CircularProgress size={16} /> : null
        }}
      />
    </Grid>
    <Grid item xs={6} md={2}>
      <TextField
        fullWidth
        type="date"
        label="Release Date"
        size="small"
        value={releaseDate ? releaseDate.toISOString().split('T')[0] : ''}
        onChange={(e) => {
          const date = new Date(e.target.value);
          if (!isNaN(date.getTime())) {
            handleReleaseDateChange(date);
          }
        }}
        InputLabelProps={{ shrink: true }}
      />
    </Grid>
    <Grid item xs={6} md={3}>
      <Button
        fullWidth
        variant="outlined"
        onClick={handleFingerprintSearch}
        disabled={searchLoading}
        sx={{ height: 34 }}
      >
        Scan Finger
      </Button>
    </Grid>
  </Grid>
</Box>

{searchResults.length > 0 && (
  <Box sx={{ mb: 1.25 }}>
    <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b', mb: 0.5 }}>
      {searchResults.length} found — click a row to open
    </Typography>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Date</TableCell>
          <TableCell>Ticket No</TableCell>
          <TableCell>Name</TableCell>
          <TableCell>Mobile</TableCell>
          <TableCell>Village</TableCell>
          <TableCell>Principal</TableCell>
          <TableCell>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {searchResults.map((pledge) => (
       <TableRow 
       key={pledge._id} 
       onClick={() => {
         // Automatically search using ticket number
         handleSearch(pledge.loanDetails.ticketNo, 'ticketNo');
         // Also load the pledge data
         setTicketNumber(pledge.loanDetails.ticketNo);
         fetchPledgeData(pledge.loanDetails.ticketNo);
       }}
       hover
       sx={{ 
         '&:hover': {
           backgroundColor: 'rgba(0,0,0,0.05)'
         }
       }}
     >
            <TableCell>
              <Typography variant="body2" color="text.secondary">
                {formatDate(pledge.date)}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography sx={{ fontWeight: 800, fontSize: 13 }}>
                {pledge.loanDetails.ticketNo}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {`${pledge.customer.firstName} ${pledge.customer.lastName}`}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {pledge.customer.mobileNo}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {pledge.customer.village}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2">
                ₹{pledge.loanDetails.principalAmount?.toLocaleString()}
              </Typography>
            </TableCell>
            <TableCell>
              <Chip 
                label={pledge.loanDetails.released ? 'Released' : 'Active'}
                color={pledge.loanDetails.released ? 'error' : 'success'}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
)}

{searchResults.length === 0 && searchValue && (
  <Alert severity="info" sx={{ mb: 4 }}>
    No pledges found matching your search criteria.
  </Alert>
)}
      <Container maxWidth={false} disableGutters sx={{ mt: 0.5, mb: 1 }}>
        <Paper sx={{ p: 0 }} elevation={0}>
          {pledgeData && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Pledge Details
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                {[
                  ['Ticket', pledgeData?.loanDetails?.ticketNo || '—'],
                  ['Principal', `₹${Number(pledgeData?.loanDetails?.principalAmount || 0).toLocaleString('en-IN')}`],
                  ['Rate', rateLabel(pledgeData, false)],
                  ['Pledge date', formatDate(pledgeData?.date)],
                  ['Days', days > 0 ? String(days) : '—'],
                ].map(([label, value]) => (
                  <Box key={label} sx={{ px: 1.25, py: 0.6, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', minWidth: 110 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8' }}>{label}</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{value}</Typography>
                  </Box>
                ))}
              </Box>
              <PledgePhotosRow
                clientPhoto={pledgeData?.clientPhoto}
                articlePhoto={pledgeData?.articlePhoto}
              />
            </Box>
          )}
          {pledgeData && releaseDate && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Interest Calculation
              </Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Received</TableCell>
                    <TableCell align="right">Pending</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Principal</TableCell>
                    <TableCell align="right">₹{pledgeData?.loanDetails?.principalAmount || 0}</TableCell>
                    <TableCell align="right">
                      {pledgeData?.receipts?.length > 0 
                        ? <span>₹{pledgeData.receipts.reduce((total, receipt) => {
                            const amount = receipt.adjustments?.adjustToPrincipal ? (receipt.amount || 0) : 0;
                            return total + amount;
                          }, 0).toFixed(2)}</span>
                        : <span>₹0</span>}
                    </TableCell>
                    <TableCell align="right">
                      {pledgeData?.receipts?.length > 0 
                        ? <span>₹{((pledgeData?.loanDetails?.principalAmount || 0) - (pledgeData.receipts.reduce((total, receipt) => {
                            const principalPaid = receipt.adjustments?.adjustToPrincipal ? (receipt.amount || 0) : 0;
                            return total + principalPaid;
                          }, 0) || 0)).toFixed(2)}</span>
                        : <span>₹{pledgeData?.loanDetails?.principalAmount || 0}</span>}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Interest</TableCell>
                    <TableCell align="right">
                      <span>₹{calculateInterest()}</span>
                    </TableCell>
                    <TableCell align="right">
                      {pledgeData?.receipts?.length > 0 
                        ? <span>₹{Math.floor(pledgeData.receipts.reduce((total, receipt) => 
                            total + (receipt.interestDetails?.calculatedInterest || 0), 
                            0
                          ))}</span>
                        : <span>₹0</span>}
                    </TableCell>
                    <TableCell align="right">
                      {pledgeData?.receipts?.length > 0 
                        ? <span>₹{Math.ceil(calculateInterest() - 
                            pledgeData.receipts.reduce((total, receipt) => 
                              total + (receipt.interestDetails?.calculatedInterest || 0), 
                              0
                            ))}</span>
                        : <span>₹{calculateInterest()}</span>}
                    </TableCell>
                  </TableRow>
                  {isBlackLogin() && (
                    <TableRow>
                      <TableCell>Secondary</TableCell>
                      <TableCell align="right">₹{Number(calculateSecondaryPosting() || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">—</TableCell>
                      <TableCell align="right">₹{Number(calculateSecondaryPosting() || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell>Total</TableCell>
                    <TableCell align="right">
                      ₹{((
                        (calculateInterest() || 0) +
                        (pledgeData?.loanDetails?.principalAmount || 0)
                      )).toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {pledgeData?.receipts?.length > 0 
                        ? (<span>₹{Math.floor(pledgeData.receipts.reduce((total, receipt) => 
                            total + (receipt.interestDetails?.calculatedInterest || 0), 
                            0
                          ))}</span>)
                        : <span>₹0</span>}
                    </TableCell>
                    <TableCell align="right">
                      {pledgeData?.receipts?.length > 0 
                        ? (<span>₹{Math.ceil(
                            (pledgeData?.loanDetails?.principalAmount || 0) +
                            (calculateInterest() || 0) -
                            (pledgeData.receipts?.reduce((total, receipt) => 
                              total + (receipt.interestDetails?.calculatedInterest || 0), 
                              0
                            ) || 0)
                          )}</span>)
                        : <span>₹{Math.ceil(
                            (pledgeData?.loanDetails?.principalAmount || 0) +
                            (calculateInterest() || 0)
                          )}</span>}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={openPaymentDialog}
                  disabled={loading || isVerifyingFingerprint || !pledgeData}
                >
                  Release
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleScanAndMatchFingerprint}
                  disabled={loading || isVerifyingFingerprint || !pledgeData}
                >
                  {isVerifyingFingerprint ? 'Scanning...' : 'Scan Finger'}
                </Button>
                {fingerprintVerified && (
                  <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 700 }}>
                    Matched{fingerprintScore !== null ? ` (${fingerprintScore})` : ''}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Paper>
      </Container>
      </GirviPageShell>

          {/* Payment Mode Dialog */}
          <Dialog
            open={showPaymentDialog}
            onClose={() => setShowPaymentDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Payment Mode — Release</DialogTitle>
            <DialogContent>
              {pledgeData && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="body2"><strong>Ticket:</strong> {pledgeData?.loanDetails?.ticketNo}</Typography>
                  <Typography variant="body2"><strong>Total Release Amount:</strong> ₹{(
                    (Number(pledgeData?.loanDetails?.principalAmount) || 0) +
                    (calculateInterest() || 0)
                  ).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
                  {(() => {
                    const totalEntered = paymentRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
                    const totalRelease = (Number(pledgeData?.loanDetails?.principalAmount) || 0) + (calculateInterest() || 0);
                    const remaining = totalRelease - totalEntered;
                    return (
                      <>
                        <Typography variant="body2"><strong>Total Entered:</strong> ₹{totalEntered.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
                        <Typography variant="body2" color={remaining > 0 ? 'warning.main' : remaining === 0 ? 'success.main' : 'error.main'}>
                          <strong>Remaining:</strong> ₹{remaining.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </Typography>
                      </>
                    );
                  })()}
                </Box>
              )}

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '25%' }}>Payment Type</TableCell>
                    <TableCell sx={{ width: '35%' }}>Bank Account</TableCell>
                    <TableCell sx={{ width: '25%' }}>Amount (₹)</TableCell>
                    <TableCell sx={{ width: '15%' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentRows.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Select
                          fullWidth
                          size="small"
                          value={row.type}
                          onChange={(e) => {
                            const updated = [...paymentRows];
                            updated[idx].type = e.target.value;
                            if (e.target.value === 'cash') {
                              updated[idx].bankLedgerCode = '';
                              updated[idx].bankLedgerName = '';
                            }
                            setPaymentRows(updated);
                          }}
                        >
                          <MenuItem value="cash">Cash</MenuItem>
                          <MenuItem value="upi">UPI</MenuItem>
                          <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {row.type !== 'cash' ? (
                          <Autocomplete
                            size="small"
                            options={bankAccounts}
                            getOptionLabel={(opt) => [opt.firstName, opt.lastName].filter(Boolean).join(' ') || opt.code || ''}
                            value={bankAccounts.find(b => b.code === row.bankLedgerCode) || null}
                            onChange={(e, val) => {
                              const updated = [...paymentRows];
                              updated[idx].bankLedgerCode = val?.code || '';
                              updated[idx].bankLedgerName = val ? [val.firstName, val.lastName].filter(Boolean).join(' ') : '';
                              setPaymentRows(updated);
                            }}
                            loading={loadingBanks}
                            renderInput={(params) => <TextField {...params} placeholder="Select bank account" />}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={row.amount}
                          onChange={(e) => {
                            const updated = [...paymentRows];
                            updated[idx].amount = e.target.value;
                            setPaymentRows(updated);
                          }}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </TableCell>
                      <TableCell>
                        {paymentRows.length > 1 && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setPaymentRows(paymentRows.filter((_, i) => i !== idx))}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setPaymentRows([...paymentRows, { type: 'cash', bankLedgerCode: '', bankLedgerName: '', amount: '' }])}
                sx={{ mt: 1 }}
              >
                Add Payment
              </Button>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
              <Button
                variant="contained"
                color="primary"
                disabled={loading}
                onClick={() => {
                  const principalAmt = Number(pledgeData?.loanDetails?.principalAmount) || 0;
                  const interestAmt = calculateInterest() || 0;
                  const totalRelease = principalAmt + interestAmt;
                  const validPayments = paymentRows
                    .filter((p) => p.amount && Number(p.amount) > 0)
                    .map((p) => ({
                      type: p.type,
                      bankLedgerCode: p.bankLedgerCode || null,
                      bankLedgerName: p.bankLedgerName || null,
                      amount: Number(p.amount)
                    }));
                  const totalEntered = validPayments.reduce((s, r) => s + (Number(r.amount) || 0), 0);
                  if (Math.abs(totalEntered - totalRelease) > 0.02) {
                    setError(
                      `Payment total (₹${totalEntered.toFixed(2)}) must equal release total (₹${totalRelease.toFixed(2)}).`
                    );
                    return;
                  }
                  setError('');
                  setReleasePhotoCustFile(null);
                  setReleasePhotoArtFile(null);
                  setPendingPaymentForRelease(validPayments);
                  setShowPaymentDialog(false);
                  setShowReleasePhotoDialog(true);
                }}
              >
                Continue to photos
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={showReleasePhotoDialog}
            onClose={() => {
              if (compressingReleasePhotos || loading) return;
              setShowReleasePhotoDialog(false);
              setPendingPaymentForRelease(null);
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Release — customer and article photos</DialogTitle>
            <DialogContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                Photos are optional. You can release without them. If you add photos, each image is compressed to <strong>2 KB or less</strong> before upload (JPEG).
              </Alert>
              <Typography variant="subtitle2" gutterBottom>
                Customer
              </Typography>
              <Button variant="outlined" component="label" sx={{ mb: 2, display: 'block' }}>
                {releasePhotoCustFile ? releasePhotoCustFile.name : 'Choose customer photo'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => setReleasePhotoCustFile(e.target.files?.[0] || null)}
                />
              </Button>
              <Typography variant="subtitle2" gutterBottom>
                Article / pledge goods
              </Typography>
              <Button variant="outlined" component="label" sx={{ display: 'block' }}>
                {releasePhotoArtFile ? releasePhotoArtFile.name : 'Choose article photo'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => setReleasePhotoArtFile(e.target.files?.[0] || null)}
                />
              </Button>
            </DialogContent>
            <DialogActions>
              <Button
                disabled={compressingReleasePhotos || loading}
                onClick={() => {
                  setShowReleasePhotoDialog(false);
                  setPendingPaymentForRelease(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                disabled={compressingReleasePhotos || loading}
                onClick={() => {
                  setShowReleasePhotoDialog(false);
                  handleCompleteTransaction(pendingPaymentForRelease || [], null, null);
                }}
              >
                Release without photos
              </Button>
              <Button
                variant="contained"
                disabled={compressingReleasePhotos || loading || !releasePhotoCustFile || !releasePhotoArtFile}
                onClick={async () => {
                  try {
                    setCompressingReleasePhotos(true);
                    setError('');
                    const c = await compressImageFileUnder10Kb(releasePhotoCustFile);
                    const a = await compressImageFileUnder10Kb(releasePhotoArtFile);
                    setReleaseCustomerPhotoDataUrl(c);
                    setReleaseArticlePhotoDataUrl(a);
                    setShowReleasePhotoDialog(false);
                    await handleCompleteTransaction(pendingPaymentForRelease || [], c, a);
                  } catch (err) {
                    setError(err.message || 'Could not compress photos');
                  } finally {
                    setCompressingReleasePhotos(false);
                  }
                }}
              >
                {compressingReleasePhotos ? 'Compressing…' : 'Save and release'}
              </Button>
            </DialogActions>
          </Dialog>

          <PrintModal />
    </>
  );
};

export default ReleasePage;